<script>
    import {killerPlayers} from "../store";
    import {createEventDispatcher, onMount, onDestroy} from "svelte";
    import {HistoryEvent} from "../tactics.model";
    import {HistoryEventType} from "../history-events.enum";
    import {KillerHistoryEvent} from "../killer.model";

    const dispatch = createEventDispatcher();
    let activePlayerIndex = 0;
    let gameFinished = false;
    let history = [];

    const letters = [
        { key: 'K', label: 'K', prop: 'kHit' },
        { key: 'I', label: 'I', prop: 'iHit' },
        { key: 'L1', label: 'L', prop: 'l1Hit' },
        { key: 'L2', label: 'L', prop: 'l2Hit' },
        { key: 'E', label: 'E', prop: 'eHit' },
        { key: 'R', label: 'R', prop: 'rHit' }
    ];

    function undo() {
        gameFinished = false;
        let lastHistoryItem = history[history.length - 1];

        if (!lastHistoryItem) {
            return; // No more history items
        }

        let player = $killerPlayers[lastHistoryItem.playerIndex];

        if (lastHistoryItem.type === HistoryEventType.SEGMENT_HIT) {
            handleCharacter(lastHistoryItem.characterHit, player);
        }

        if (lastHistoryItem.type === HistoryEventType.LIVE_TAKEN) {
            player.lives = player.lives + 1;
            player.eliminated = false;
        }

        history = history.slice(0, -1);
        killerPlayers.update(players => {
            return [...players];
        });

        if (lastHistoryItem.type === HistoryEventType.NEXT_PLAYER) {
            if (activePlayerIndex - 1 < 0) {
                activePlayerIndex = $killerPlayers.length - 1;
            } else {
                activePlayerIndex = activePlayerIndex - 1;
            }

            undo();
        }
    }

    function reset() {
        gameFinished = false;
        activePlayerIndex = 0;
        history = [];
        dispatch('reset');
    }

    function newGame() {
        gameFinished = false;
        activePlayerIndex = 0;
        history = [];
        dispatch('newGame');
    }

    function handleCharacter(character, player) {
        switch (character) {
            case 'K':
                player.kHit = !player.kHit;
                break;
            case 'I':
                player.iHit = !player.iHit;
                break;
            case 'L1':
                player.l1Hit = !player.l1Hit;
                break;
            case 'L2':
                player.l2Hit = !player.l2Hit;
                break;
            case 'E':
                player.eHit = !player.eHit;
                break;
            case 'R':
                player.rHit = !player.rHit;
                break;
            default:
        }
    }

    function handleKillerHit(player, character) {
        if (gameFinished) return;
        handleCharacter(character, player);

        killerPlayers.update(players => {
            players[activePlayerIndex].kHit = player.kHit;
            players[activePlayerIndex].iHit = player.iHit;
            players[activePlayerIndex].l1Hit = player.l1Hit;
            players[activePlayerIndex].l2Hit = player.l2Hit;
            players[activePlayerIndex].eHit = player.eHit;
            players[activePlayerIndex].rHit = player.rHit;
            return [...players];
        });

        history = [...history, new KillerHistoryEvent(HistoryEventType.SEGMENT_HIT, activePlayerIndex, character, player.lives)];
    }

    function checkWinner() {
        let activePlayers = $killerPlayers.filter(player => !player.eliminated);

        if (activePlayers.length === 1 && $killerPlayers.length > 1) {
            gameFinished = true;
        }
    }

    function nextPlayer() {
        if (gameFinished) return;
        const total = $killerPlayers.length;
        if (total === 0) return;

        let nextIdx = (activePlayerIndex + 1) % total;
        let loopCount = 0;
        while ($killerPlayers[nextIdx].eliminated && loopCount < total) {
            nextIdx = (nextIdx + 1) % total;
            loopCount++;
        }
        activePlayerIndex = nextIdx;
        history = [...history, new HistoryEvent(HistoryEventType.NEXT_PLAYER, activePlayerIndex, 0)];
    }

    function handleLives(playerToRemoveLife) {
        if (gameFinished) return;
        const lives = Math.max(0, playerToRemoveLife.lives - 1);
        let playerToUpdate = $killerPlayers.find(player => player.name === playerToRemoveLife.name);
        if (!playerToUpdate) return;
        const playerIndex = $killerPlayers.indexOf(playerToUpdate);

        killerPlayers.update(players => {
            players[playerIndex].lives = lives;
            players[playerIndex].eliminated = (players[playerIndex].lives === 0);
            return [...players];
        });

        history = [...history, new KillerHistoryEvent(HistoryEventType.LIVE_TAKEN, playerIndex, null, lives)];
        checkWinner();
    }

    function isPlayerActive(player, finished = gameFinished, activeIdx = activePlayerIndex) {
        return !finished && $killerPlayers[activeIdx]?.name === player?.name;
    }

    function isPlayerWinner(player, finished = gameFinished, players = $killerPlayers) {
        if (!finished || !players || players.length === 0) return false;
        const activeRemaining = players.filter(p => !p.eliminated);
        return activeRemaining.length === 1 && activeRemaining[0]?.name === player?.name;
    }

    function canKill(targetPlayer, finished = gameFinished, activeIdx = activePlayerIndex, players = $killerPlayers) {
        if (finished || !players || players.length === 0) return false;
        const activePlayer = players[activeIdx];
        if (!activePlayer || activePlayer.eliminated || !activePlayer.isActiveKiller()) return false;
        return !targetPlayer.eliminated;
    }

    function handleKeydown(event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            nextPlayer();
        }
    }

    onMount(() => {
        window.addEventListener('keydown', handleKeydown);
    });

    onDestroy(() => {
        window.removeEventListener('keydown', handleKeydown);
    });
</script>

<div class="killer-screen w-full h-screen min-h-screen text-slate-900 font-sans antialiased flex flex-col p-3 sm:p-4 md:p-5 lg:p-6 box-border bg-[#f0ece9]">
    <!-- BEGIN: TopControlsBar -->
    <header class="w-full max-w-[1600px] mx-auto bg-white rounded-2xl sm:rounded-3xl shadow-sm px-4 sm:px-6 py-3 mb-3 sm:mb-4 border border-black/5 flex flex-wrap items-center justify-between gap-4 shrink-0">
        <!-- Brand & Game Badge -->
        <div class="flex items-center gap-4 sm:gap-6 min-w-[200px]">
            <button class="inline-flex items-center transition-opacity hover:opacity-85 focus:outline-none" on:click={newGame} title="Yuma Home">
                <img alt="Yuma Logo" class="h-8 sm:h-9 w-auto object-contain" src="images/yuma.png" />
            </button>
            <div class="h-8 w-px bg-slate-200"></div>
        </div>

        <!-- Center Controls: Next Player -->
        <div class="flex items-center justify-center flex-1 max-w-md mx-auto order-last md:order-none w-full md:w-auto">
            <div class="flex items-center gap-2 bg-stone-100/80 p-1.5 rounded-2xl border border-slate-200 shadow-inner w-full sm:w-auto justify-center">
                <button
                    class="h-11 inline-flex items-center justify-center gap-2 bg-[#eba9ff] hover:bg-[#e493fb] active:scale-[0.98] text-[#005d46] font-bold px-6 sm:px-8 rounded-xl transition-all shadow-sm border border-purple-300/30 whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    disabled={gameFinished}
                    on:click={nextPlayer}
                    type="button"
                    title="Next player (shortcut: Enter)"
                >
                    <span>Next player</span>
                    <span class="material-symbols-outlined text-[18px]">arrow_forward</span>
                </button>
            </div>
        </div>

        <!-- Right Quick Actions (Undo, Reset Round, Home) -->
        <div class="flex items-center justify-end gap-2.5 min-w-[200px]">
            <!-- Undo Button -->
            <button
                class="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-pink-50 hover:bg-pink-100 text-rose-600 transition-colors border border-rose-200/50 cursor-pointer active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={history.length === 0}
                on:click={undo}
                title="Undo last action"
                type="button"
            >
                <span class="material-symbols-outlined text-[20px]">undo</span>
            </button>
            <!-- New Round Button -->
            <button
                class="h-11 inline-flex items-center gap-2 bg-[#6434da] hover:bg-[#5229b5] text-white text-sm font-semibold px-4 rounded-xl transition-colors shadow-sm cursor-pointer active:scale-95"
                on:click={reset}
                title="Reset game"
                type="button"
            >
                <span class="material-symbols-outlined text-[18px]">restart_alt</span>
                <span class="hidden sm:inline">New round</span>
            </button>
            <!-- Exit to Setup / Lobby -->
            <button
                class="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors border border-slate-200 cursor-pointer active:scale-95"
                on:click={newGame}
                title="Exit to Setup"
                type="button"
            >
                <span class="material-symbols-outlined text-[20px]">home</span>
            </button>
        </div>
    </header>
    <!-- END: TopControlsBar -->

    <!-- BEGIN: Main Table Container -->
    <main class="w-full max-w-[1600px] mx-auto flex-1 min-h-0 bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-black/5 flex flex-col overflow-hidden">
        <div class="flex-1 min-h-0 overflow-auto">
            <table class="killer-table w-full h-full flex-1 border-collapse select-none">
                <!-- THEAD -->
                <thead>
                    <tr class="h-12 sm:h-14 text-white text-lg bg-[#005d46]">
                        <th class="w-[22%] min-w-[180px] bg-[#005d46] text-white p-3 sm:p-4 text-left align-middle border-r border-emerald-800/40" style="text-align: left;">
                            <div class="flex items-center justify-start gap-2 sm:gap-3 text-left">
                                <span class="material-symbols-outlined text-[#21e467] text-[24px] sm:text-[28px] shrink-0">sports_kabaddi</span>
                                <div class="text-left">
                                    <div class="text-base sm:text-lg md:text-xl font-black font-headline tracking-tight text-white leading-tight">
                                        Killer
                                    </div>
                                    <div class="text-[11px] sm:text-xs text-emerald-200/80 font-medium">
                                        Hit your number to become Killer
                                    </div>
                                </div>
                            </div>
                        </th>
                        <th class="w-[12%] min-w-[90px] px-3 py-2.5 text-center align-middle border-r border-emerald-800/40 text-sm sm:text-base font-bold font-headline tracking-wider text-white" style="text-align: center;">
                            Target
                        </th>
                        <th class="w-[36%] min-w-[280px] px-3 py-2.5 text-center align-middle border-r border-emerald-800/40 text-sm sm:text-base font-bold font-headline tracking-wider text-white" style="text-align: center;">
                           
                        </th>
                        <th class="w-[18%] min-w-[150px] px-3 py-2.5 text-center align-middle border-r border-emerald-800/40 text-sm sm:text-base font-bold font-headline tracking-wider text-white" style="text-align: center;">
                            Lives
                        </th>
                        <th class="w-[12%] min-w-[100px] px-3 py-2.5 text-center align-middle text-sm sm:text-base font-bold font-headline tracking-wider text-white" style="text-align: center;">
                            Kill
                        </th>
                    </tr>
                </thead>

                <!-- TBODY -->
                <tbody>
                    {#each $killerPlayers as player}
                        <tr class="player-row transition-colors border-b last:border-b-0 border-slate-200/80 {
                            isPlayerActive(player, gameFinished, activePlayerIndex)
                                ? 'bg-amber-50/40 hover:bg-amber-50/60'
                                : 'bg-white hover:bg-stone-50'
                        }">
                            <!-- Player Name Column -->
                            <td class="px-4 py-2 text-left align-middle border-r border-slate-200 transition-colors {
                                isPlayerWinner(player, gameFinished, $killerPlayers)
                                    ? 'bg-[#21e467] text-[#005d46]'
                                    : isPlayerActive(player, gameFinished, activePlayerIndex)
                                        ? 'bg-[#fff8e7] text-amber-950 border-r border-amber-200/80 shadow-inner'
                                        : player.eliminated
                                            ? 'bg-slate-50/80 text-slate-400'
                                            : 'text-slate-900'
                            }">
                                <div class="flex items-center gap-2.5 flex-wrap">
                                    {#if isPlayerActive(player, gameFinished, activePlayerIndex) && !isPlayerWinner(player, gameFinished, $killerPlayers)}
                                        <span class="inline-block w-2.5 h-2.5 rounded-full bg-[#21e467] border-2 border-emerald-700 animate-pulse"></span>
                                    {/if}
                                    <span class="text-xl sm:text-2xl font-bold font-headline truncate {player.eliminated ? 'line-through opacity-70' : ''}">
                                        {player.name}
                                    </span>
                                    {#if isPlayerWinner(player, gameFinished, $killerPlayers)}
                                        <span class="text-xl sm:text-2xl leading-none select-none inline-flex items-center">
                                            🏆
                                        </span>
                                    {/if}
                                    {#if player.eliminated && !isPlayerWinner(player, gameFinished, $killerPlayers)}
                                        <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-700 border border-rose-200 uppercase tracking-wider">
                                            Out
                                        </span>
                                    {/if}
                                </div>
                            </td>

                            <!-- Target Number Column -->
                            <td class="px-3 py-2 text-center align-middle border-r border-slate-200" style="text-align: center;">
                                <div class="flex items-center justify-center">
                                    <span class="text-black text-3xl sm:text-4xl md:text-5xl font-black font-headline leading-none">
                                        {player.number}
                                    </span>
                                </div>
                            </td>

                            <!-- KILLER Letters Column -->
                            <td class="px-3 py-2 text-center align-middle border-r border-slate-200">
                                <div class="flex items-center justify-center gap-1.5 sm:gap-2 flex-wrap">
                                    {#each letters as letter}
                                        <button
                                            type="button"
                                            class="w-8 h-8 sm:w-10 sm:h-10 md:w-11 md:h-11 rounded-xl font-headline font-black text-sm sm:text-base md:text-lg transition-all shadow-xs border flex items-center justify-center {
                                                player[letter.prop]
                                                    ? 'bg-rose-600 hover:bg-rose-700 text-white border-rose-700 shadow-sm'
                                                    : 'bg-stone-100 hover:bg-stone-200 text-slate-600 border-slate-200'
                                            } {
                                                $killerPlayers[activePlayerIndex]?.name === player?.name && !gameFinished && !player.eliminated
                                                    ? 'cursor-pointer active:scale-95 ring-1 ring-slate-300'
                                                    : 'cursor-default opacity-85'
                                            }"
                                            disabled="{$killerPlayers[activePlayerIndex]?.name !== player?.name || gameFinished || player.eliminated}"
                                            on:click={() => handleKillerHit(player, letter.key)}
                                            title="{$killerPlayers[activePlayerIndex]?.name === player?.name ? `Toggle letter ${letter.label}` : letter.label}"
                                        >
                                            {letter.label}
                                        </button>
                                    {/each}

                                    {#if player.isActiveKiller() && !player.eliminated}
                                        <span class="ml-1 px-2 sm:px-2.5 py-1 rounded-lg bg-rose-600 text-white font-black text-[11px] sm:text-xs tracking-wider uppercase shadow-xs flex items-center gap-1 animate-pulse">
                                            <span class="material-symbols-outlined text-[14px]">skull</span>
                                            <span>KILLER</span>
                                        </span>
                                    {/if}
                                </div>
                            </td>

                            <!-- Lives Column -->
                            <td class="px-3 py-2 text-center align-middle border-r border-slate-200">
                                <div class="flex items-center justify-center gap-1 sm:gap-1.5 flex-wrap">
                                    {#if player.eliminated}
                                        <span class="px-3 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-700 border border-rose-300 uppercase tracking-wider">
                                            Eliminated
                                        </span>
                                    {:else}
                                        {#each Array(player.lives) as _}
                                            <svg class="w-6 h-6 sm:w-7 sm:h-7 fill-red-600 text-red-600 drop-shadow-xs" viewBox="0 0 24 24" style="fill: #dc2626;">
                                                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                                            </svg>
                                        {/each}
                                        {#each Array(Math.max(0, 5 - player.lives)) as _}
                                            <svg class="w-6 h-6 sm:w-7 sm:h-7 fill-slate-200 text-slate-200 opacity-60" viewBox="0 0 24 24" style="fill: #e2e8f0;">
                                                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                                            </svg>
                                        {/each}
                                    {/if}
                                </div>
                            </td>

                            <!-- Kill Action Column -->
                            <td class="px-3 py-2 text-center align-middle">
                                <div class="flex items-center justify-center">
                                    <button
                                        type="button"
                                        class="h-10 px-3 sm:px-4 inline-flex items-center justify-center gap-1.5 rounded-xl font-bold text-xs sm:text-sm transition-all shadow-xs border cursor-pointer active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed {
                                            canKill(player, gameFinished, activePlayerIndex, $killerPlayers)
                                                ? 'bg-rose-600 hover:bg-rose-700 text-white border-rose-700 shadow-md animate-pulse'
                                                : 'bg-slate-100 text-slate-400 border-slate-200'
                                        }"
                                        disabled={!canKill(player, gameFinished, activePlayerIndex, $killerPlayers)}
                                        on:click={() => handleLives(player)}
                                        title={canKill(player, gameFinished, activePlayerIndex, $killerPlayers) ? `Take 1 life from ${player.name}` : 'Must be an active Killer to attack'}
                                    >
                                        <span class="material-symbols-outlined text-[18px]">skull</span>
                                        <span class="hidden sm:inline">Kill</span>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>

        <!-- Footer Info Strip -->
        <footer class="bg-stone-50 border-t border-slate-200 px-4 sm:px-6 py-2.5 flex items-center justify-between text-xs text-slate-500 font-medium shrink-0">
            <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-[16px] text-emerald-700">info</span>
                <span>Hit your number 6 times to unlock K-I-L-L-E-R. Once a Killer, hit other players' numbers to take their lives!</span>
            </div>
            <div class="hidden md:flex items-center gap-4">
                <span class="text-slate-400">Shortcut: <strong class="text-slate-600">Enter</strong> for Next player</span>
            </div>
        </footer>
    </main>
</div>

<style>
    .killer-table {
        table-layout: fixed;
        width: 100%;
    }

    .killer-table th,
    .killer-table td {
        vertical-align: middle !important;
    }

    .killer-table th.text-center,
    .killer-table td.text-center {
        text-align: center !important;
    }

    .player-row {
        height: 1%;
    }
</style>
